/**
 * HoursCompact — grouped day-range rows (Mon–Fri 9–5 style).
 * Space-efficient; good for tight layouts, footers, or sidebar use.
 *
 * Registered as: hours / hours-compact
 */
import React, { useEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import type { SectionComponentProps } from "../../registry.js";
import { logConversionEvent } from "@repo/backend/firebase/analytics";

gsap.registerPlugin(ScrollTrigger);

interface HoursRange {
  days: string;
  hours: string;
}

interface HoursCompactProps {
  headline?: string;
  /** Grouped ranges, e.g. [{ days: "Mon – Fri", hours: "8am – 6pm" }, ...] */
  ranges?: HoursRange[];
  phone?: string;
  address?: string;
  note?: string;
}

const DEFAULT_RANGES: HoursRange[] = [
  { days: "Mon – Fri", hours: "8:00 AM – 6:00 PM" },
  { days: "Saturday", hours: "9:00 AM – 4:00 PM" },
  { days: "Sunday", hours: "Closed" },
];

export function HoursCompact({ props }: SectionComponentProps) {
  const {
    headline = "Hours",
    ranges = DEFAULT_RANGES,
    phone,
    address,
    note,
  } = props as HoursCompactProps;

  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const ctx = gsap.context(() => {
      gsap.fromTo(
        el,
        { opacity: 0, y: 24 },
        {
          opacity: 1, y: 0, duration: 0.65, ease: "power2.out",
          scrollTrigger: { trigger: el, start: "top 84%", toggleActions: "play none none none" },
        },
      );
    }, el);

    return () => ctx.revert();
  }, []);

  return (
    <section ref={sectionRef} className="py-14 px-6 bg-muted/30" id="hours">
      <div className="max-w-xl mx-auto">
        <h2 className="text-xl font-bold text-foreground mb-6">{headline}</h2>

        <div className="space-y-0">
          {ranges.map((range, i) => (
            <div
              key={i}
              className="flex items-baseline justify-between py-2.5 border-b border-border last:border-0 gap-4"
            >
              <span className="text-sm text-foreground font-medium whitespace-nowrap">{range.days}</span>
              <span
                className={`text-sm text-right ${
                  range.hours.toLowerCase() === "closed" ? "text-muted-foreground/60" : "text-muted-foreground"
                }`}
              >
                {range.hours}
              </span>
            </div>
          ))}
        </div>

        {note && (
          <p className="mt-4 text-xs text-muted-foreground italic">{note}</p>
        )}

        {(phone || address) && (
          <div className="mt-6 pt-4 border-t border-border space-y-1 text-sm text-muted-foreground">
            {address && <p>{address}</p>}
            {phone && (
              <a href={`tel:${phone}`} onClick={() => logConversionEvent("click_call", { phone })} className="text-primary hover:underline font-medium block">
                {phone}
              </a>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
